import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { getProducts, exportProducts, reEvaluateProduct } from '../api/products';
import { getIngredients } from '../api/ingredients';
import { invalidateBarcode } from '../api/cache';
import { useToast } from './toast';
import type { TabId } from '../layout/sidebar';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (tab: TabId) => void;
}

type CommandMode = 'search' | 'invalidate-cache' | 're-evaluate';

interface CommandItem {
  id: string;
  label: string;
  section: string;
  icon: string;
  action: () => void;
  keywords: string[];
  hint?: string;
}

interface EntityResult {
  id: string;
  label: string;
  sublabel?: string;
  type: 'product' | 'ingredient';
  navigateTo: string;
}

function SvgIcon({ path }: { path: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      dangerouslySetInnerHTML={{ __html: path }}
    />
  );
}

function fuzzyMatch(query: string, target: string): boolean {
  const q = query.toLowerCase();
  const t = target.toLowerCase();
  if (t.includes(q)) return true;
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
}

export function CommandPalette({ open, onClose, onNavigate }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [mode, setMode] = useState<CommandMode>('search');
  const [inlineInput, setInlineInput] = useState('');
  const [inlineLoading, setInlineLoading] = useState(false);
  const [entityResults, setEntityResults] = useState<EntityResult[]>([]);
  const [entityLoading, setEntityLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toast = useToast();

  // ── Action commands ──────────────────────────────────
  const handleInvalidateCache = useCallback(() => {
    setMode('invalidate-cache');
    setInlineInput('');
    setQuery('');
  }, []);

  const handleExportCSV = useCallback(async () => {
    onClose();
    try {
      const blob = await exportProducts();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV export started');
    } catch {
      toast.error('Failed to export CSV');
    }
  }, [onClose, toast]);

  const handleAddTip = useCallback(() => {
    onNavigate('tips');
    onClose();
  }, [onNavigate, onClose]);

  const handleReEvaluate = useCallback(() => {
    setMode('re-evaluate');
    setInlineInput('');
    setQuery('');
  }, []);

  const executeInlineAction = useCallback(async () => {
    const value = inlineInput.trim();
    if (!value) return;
    setInlineLoading(true);
    try {
      if (mode === 'invalidate-cache') {
        const res = await invalidateBarcode(value);
        toast.success(`Deleted ${res.deleted} cache entries for ${value}`);
      } else if (mode === 're-evaluate') {
        await reEvaluateProduct(value);
        toast.success(`Re-evaluation triggered for ${value}`);
      }
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setInlineLoading(false);
    }
  }, [mode, inlineInput, onClose, toast]);

  const commands: CommandItem[] = [
    // Navigation
    { id: 'nav-overview', label: 'Go to Overview', section: 'Navigation', icon: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>', action: () => onNavigate('overview'), keywords: ['dashboard', 'home', 'overview'] },
    { id: 'nav-products', label: 'Go to Products', section: 'Navigation', icon: '<path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>', action: () => onNavigate('products'), keywords: ['products', 'items', 'food'] },
    { id: 'nav-ingredients', label: 'Go to Ingredients', section: 'Navigation', icon: '<path d="M10 2v7.53a2 2 0 0 1-.2.9L4.7 20.5a1 1 0 0 0 .9 1.5h12.8a1 1 0 0 0 .9-1.5l-5.1-10.07a2 2 0 0 1-.2-.9V2"/><path d="M8.5 2h7"/><path d="M7 16.5h10"/>', action: () => onNavigate('ingredients'), keywords: ['ingredients', 'allergens'] },
    { id: 'nav-users', label: 'Go to Users', section: 'Navigation', icon: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>', action: () => onNavigate('users'), keywords: ['users', 'accounts', 'people'] },
    { id: 'nav-verdicts', label: 'Go to Verdicts', section: 'Navigation', icon: '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>', action: () => onNavigate('verdicts'), keywords: ['verdicts', 'halal', 'status'] },
    { id: 'nav-tips', label: 'Go to Tips', section: 'Navigation', icon: '<path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>', action: () => onNavigate('tips'), keywords: ['tips', 'advice'] },
    { id: 'nav-system', label: 'Go to System', section: 'Navigation', icon: '<path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"/>', action: () => onNavigate('system'), keywords: ['system', 'health', 'cache', 'logs'] },
    { id: 'nav-schema', label: 'Go to DB Schema', section: 'Navigation', icon: '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v4a9 3 0 0 0 18 0V5"/><path d="M3 9v4a9 3 0 0 0 18 0V9"/><path d="M3 13v4a9 3 0 0 0 18 0v-4"/>', action: () => onNavigate('schema'), keywords: ['schema', 'database', 'erd', 'tables'] },
    { id: 'nav-version', label: 'Go to Version', section: 'Navigation', icon: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>', action: () => onNavigate('version'), keywords: ['version', 'settings', 'update'] },
    // Actions
    { id: 'act-invalidate', label: 'Invalidate cache for barcode...', section: 'Actions', icon: '<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>', action: handleInvalidateCache, keywords: ['invalidate', 'cache', 'barcode', 'clear'] },
    { id: 'act-export', label: 'Export products CSV', section: 'Actions', icon: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>', action: handleExportCSV, keywords: ['export', 'csv', 'download', 'products'] },
    { id: 'act-add-tip', label: 'Add a new tip', section: 'Actions', icon: '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>', action: handleAddTip, keywords: ['add', 'tip', 'new', 'create'] },
    { id: 'act-reevaluate', label: 'Re-evaluate product...', section: 'Actions', icon: '<polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>', action: handleReEvaluate, keywords: ['re-evaluate', 'reevaluate', 'product', 'barcode', 'refresh'] },
  ];

  // ── Entity search (debounced) ────────────────────────
  useEffect(() => {
    if (mode !== 'search') return;
    if (searchTimer.current) clearTimeout(searchTimer.current);

    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setEntityResults([]);
      setEntityLoading(false);
      return;
    }

    setEntityLoading(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const [prodRes, ingRes] = await Promise.all([
          getProducts({ page: 1, limit: 5, search: trimmed }),
          getIngredients({ page: 1, limit: 5, search: trimmed }),
        ]);

        const results: EntityResult[] = [];
        for (const p of prodRes.products) {
          results.push({
            id: `product-${p.barcode}`,
            label: p.name ?? p.barcode,
            sublabel: p.brand ?? p.barcode,
            type: 'product',
            navigateTo: `catalog/products/${p.barcode}`,
          });
        }
        for (const i of ingRes.ingredients) {
          results.push({
            id: `ingredient-${i.id}`,
            label: i.canonical_name,
            sublabel: i.e_number ?? undefined,
            type: 'ingredient',
            navigateTo: `catalog/ingredients/${i.id}`,
          });
        }
        setEntityResults(results);
      } catch {
        setEntityResults([]);
      } finally {
        setEntityLoading(false);
      }
    }, 300);

    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [query, mode]);

  // ── Filtered commands ────────────────────────────────
  const filtered = query.trim()
    ? commands.filter((cmd) => {
        const searchTarget = [cmd.label, ...cmd.keywords].join(' ');
        return fuzzyMatch(query.trim(), searchTarget);
      })
    : commands;

  // Group by section
  const sections = new Map<string, CommandItem[]>();
  for (const item of filtered) {
    const existing = sections.get(item.section) ?? [];
    existing.push(item);
    sections.set(item.section, existing);
  }

  // Build flat list of all selectable items (commands + entity results)
  const allItems: { type: 'command'; item: CommandItem }[] | { type: 'entity'; item: EntityResult }[] = [];
  const flatItems: Array<{ type: 'command'; item: CommandItem } | { type: 'entity'; item: EntityResult }> = [];
  for (const cmd of filtered) {
    flatItems.push({ type: 'command', item: cmd });
  }
  for (const entity of entityResults) {
    flatItems.push({ type: 'entity', item: entity });
  }

  const execute = useCallback((entry: typeof flatItems[number]) => {
    if (entry.type === 'command') {
      entry.item.action();
      onClose();
    } else {
      window.location.hash = entry.item.navigateTo;
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setMode('search');
      setInlineInput('');
      setEntityResults([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, entityResults.length]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (mode !== 'search') {
        setMode('search');
        setInlineInput('');
        return;
      }
      onClose();
      return;
    }
    if (mode !== 'search') {
      if (e.key === 'Enter') {
        e.preventDefault();
        executeInlineAction();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, flatItems.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    }
    if (e.key === 'Enter' && flatItems[activeIndex]) {
      e.preventDefault();
      execute(flatItems[activeIndex]);
    }
  }, [flatItems, activeIndex, execute, onClose, mode, executeInlineAction]);

  useEffect(() => {
    // Scroll active item into view
    const el = listRef.current?.querySelector('.cmd-palette-item.active');
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  if (!open) return null;

  // ── Inline input mode (invalidate-cache, re-evaluate) ──
  if (mode !== 'search') {
    const modeLabel = mode === 'invalidate-cache'
      ? 'Enter barcode to invalidate cache'
      : 'Enter barcode to re-evaluate';

    return (
      <div class="cmd-palette-backdrop" onClick={() => { setMode('search'); onClose(); }}>
        <div class="cmd-palette" onClick={(e) => e.stopPropagation()}>
          <div class="cmd-palette-input-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 10 4 15 9 20" />
              <path d="M20 4v7a4 4 0 0 1-4 4H4" />
            </svg>
            <input
              ref={inputRef}
              class="cmd-palette-input"
              type="text"
              placeholder={modeLabel}
              value={inlineInput}
              onInput={(e) => setInlineInput((e.target as HTMLInputElement).value)}
              onKeyDown={handleKeyDown}
              disabled={inlineLoading}
            />
          </div>
          <div class="cmd-palette-list" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {mode === 'invalidate-cache'
                ? 'This will delete all cached analysis data for the given barcode.'
                : 'This will trigger a fresh AI evaluation for the given barcode.'}
            </div>
          </div>
          <div class="cmd-palette-footer">
            <span><kbd>↵</kbd> execute</span>
            <span><kbd>esc</kbd> back</span>
            {inlineLoading && <span style={{ color: 'var(--accent)' }}>Processing...</span>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div class="cmd-palette-backdrop" onClick={onClose}>
      <div class="cmd-palette" onClick={(e) => e.stopPropagation()}>
        <div class="cmd-palette-input-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            ref={inputRef}
            class="cmd-palette-input"
            type="text"
            placeholder="Search commands, products, ingredients..."
            value={query}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div class="cmd-palette-list" ref={listRef}>
          {flatItems.length === 0 && !entityLoading ? (
            <div class="cmd-palette-empty">No results found</div>
          ) : (
            <>
              {/* Command sections */}
              {Array.from(sections.entries()).map(([section, items]) => (
                <div key={section}>
                  <div class="cmd-palette-section">{section}</div>
                  {items.map((item) => {
                    const globalIdx = flatItems.findIndex(
                      (f) => f.type === 'command' && f.item.id === item.id
                    );
                    return (
                      <div
                        key={item.id}
                        class={`cmd-palette-item ${globalIdx === activeIndex ? 'active' : ''}`}
                        onClick={() => execute(flatItems[globalIdx])}
                        onMouseEnter={() => setActiveIndex(globalIdx)}
                      >
                        <SvgIcon path={item.icon} />
                        <span class="cmd-palette-item-label">{item.label}</span>
                        {item.hint && <span class="cmd-palette-item-hint">{item.hint}</span>}
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Entity search results */}
              {entityResults.length > 0 && (
                <div>
                  <div class="cmd-palette-section">Search Results</div>
                  {entityResults.map((entity) => {
                    const globalIdx = flatItems.findIndex(
                      (f) => f.type === 'entity' && f.item.id === entity.id
                    );
                    return (
                      <div
                        key={entity.id}
                        class={`cmd-palette-item ${globalIdx === activeIndex ? 'active' : ''}`}
                        onClick={() => execute(flatItems[globalIdx])}
                        onMouseEnter={() => setActiveIndex(globalIdx)}
                      >
                        <SvgIcon
                          path={
                            entity.type === 'product'
                              ? '<path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>'
                              : '<path d="M10 2v7.53a2 2 0 0 1-.2.9L4.7 20.5a1 1 0 0 0 .9 1.5h12.8a1 1 0 0 0 .9-1.5l-5.1-10.07a2 2 0 0 1-.2-.9V2"/><path d="M8.5 2h7"/><path d="M7 16.5h10"/>'
                          }
                        />
                        <span class="cmd-palette-item-label">{entity.label}</span>
                        {entity.sublabel && entity.sublabel !== entity.label && (
                          <span class="cmd-palette-item-hint">{entity.sublabel}</span>
                        )}
                        <span class={`cmd-palette-badge cmd-palette-badge-${entity.type}`}>
                          {entity.type}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Loading indicator for entity search */}
              {entityLoading && (
                <div class="cmd-palette-item" style={{ justifyContent: 'center', color: 'var(--text-tertiary)' }}>
                  <span class="spinner" style={{ width: '14px', height: '14px', borderWidth: '1.5px' }} />
                  <span style={{ fontSize: '0.8rem' }}>Searching...</span>
                </div>
              )}
            </>
          )}
        </div>
        <div class="cmd-palette-footer">
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>↵</kbd> select</span>
          <span><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
